import React, { useState, useEffect, useRef } from "react";
import { PDFDocument, rgb, StandardFonts, PDFFont } from "pdf-lib";
// @ts-ignore
import fontkit from "@pdf-lib/fontkit";
import { Button, Stack, Paper, Text, Group, Code } from "@mantine/core";
import { generateMetaPage } from "../lib/meta-page";
import { generateIntroPage } from "../lib/pdf-intro";
import { loadPdfTheme } from "../lib/pdf-theme";
import { renderFieldToImage } from "../lib/pdf-utils";
import type { SigningMetadata, SignatureEntry } from "../lib/signing-metadata";

/**
 * Meta Page Layout Debugger
 *
 * This component generates a fully populated mock PDF with the Certificate of Signature
 * page so you can visually debug and adjust the layout in meta-page.ts
 */
export function MetaPageDebugger() {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState<Date | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Mock data - fully populated with realistic values
  const mockMetadata: SigningMetadata = {
    version: 1,
    documentHash:
      "a1b2c3d4e5f6789012345678901234567890abcdefghijklmnopqrstuvwxyz",
    createdAt: "2026-02-02T10:30:00.000Z",
    signers: [
      { email: "john.doe@company.com", name: "John Doe" },
      { email: "jane.smith@partner.org", name: "Jane Smith" },
      { email: "bob.wilson@client.net", name: "Bob Wilson" },
    ],
    fields: [
      {
        id: "f1",
        page: 1,
        type: "signature",
        signerIndex: 0,
        x: 50,
        y: 100,
        width: 100,
        height: 30,
      },
      {
        id: "f2",
        page: 1,
        type: "initials",
        signerIndex: 0,
        x: 50,
        y: 150,
        width: 50,
        height: 30,
      },
      {
        id: "f3",
        page: 1,
        type: "name",
        signerIndex: 0,
        x: 50,
        y: 200,
        width: 100,
        height: 20,
      },
      {
        id: "f4",
        page: 1,
        type: "date-now",
        signerIndex: 0,
        x: 50,
        y: 230,
        width: 80,
        height: 20,
      },
      {
        id: "f5",
        page: 1,
        type: "checkbox",
        signerIndex: 0,
        x: 50,
        y: 260,
        width: 15,
        height: 15,
      },
      {
        id: "f6",
        page: 1,
        type: "signature",
        signerIndex: 1,
        x: 200,
        y: 100,
        width: 100,
        height: 30,
      },
      {
        id: "f7",
        page: 1,
        type: "name",
        signerIndex: 1,
        x: 200,
        y: 150,
        width: 100,
        height: 20,
      },
      {
        id: "f8",
        page: 1,
        type: "text",
        signerIndex: 1,
        x: 200,
        y: 180,
        width: 100,
        height: 20,
        labelText: "Company Name",
      },
      {
        id: "f9",
        page: 1,
        type: "signature",
        signerIndex: 2,
        x: 350,
        y: 100,
        width: 100,
        height: 30,
      },
      {
        id: "f10",
        page: 1,
        type: "date",
        signerIndex: 2,
        x: 350,
        y: 150,
        width: 80,
        height: 20,
      },
    ],
    signatures: [],
    creatorSignature: {
      keyId: "creator-key-abc123",
      signature: "creator-sig-xyz789",
      serverSignature: {
        signerEmail: "creator@company.com",
        signerKeyId: "creator-key-abc123",
        docHash: "mock-doc-hash-abc123",
        ip: "192.168.1.100",
        location: "San Francisco, CA, USA",
        timestamp: "2026-02-02T10:30:00.000Z",
        serverKeyId: "alpha-1",
        verified: true,
        witnessed: true,
        signature: "mock-ecdsa-sig-creator",
      },
    },
  };

  const mockSignatures: SignatureEntry[] = [
    {
      email: "john.doe@company.com",
      keyId: "pgp-key-john-abc123def456",
      signature:
        "sig-john-xyz789abc123def456ghi789jkl012mno345pqr678stu901vwx234yz",
      timestamp: "2026-02-02T11:15:30.000Z",
      serverSignature: {
        signerEmail: "john.doe@company.com",
        signerKeyId: "pgp-key-john-abc123def456",
        docHash: "mock-doc-hash-abc123",
        ip: "203.0.113.42",
        location: "New York, NY, USA",
        timestamp: "2026-02-02T11:15:30.000Z",
        serverKeyId: "alpha-1",
        verified: true,
        witnessed: true,
        signature: "mock-ecdsa-sig-john",
        userAgent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)",
      },
      sessionInput: {
        f1: {
          fieldId: "f1",
          type: "signature" as const,
          value:
            '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="60"><text x="10" y="40" font-family="cursive" font-size="32" fill="darkblue">John Doe</text></svg>',
          fontFamily: "cursive",
        },
        f2: {
          fieldId: "f2",
          type: "initials" as const,
          value:
            '<svg xmlns="http://www.w3.org/2000/svg" width="100" height="60"><text x="10" y="40" font-family="cursive" font-size="28" fill="darkblue">JD</text></svg>',
          fontFamily: "cursive",
        },
        f3: {
          fieldId: "f3",
          type: "name" as const,
          value: "John Doe",
        },
        f4: {
          fieldId: "f4",
          type: "date-now" as const,
          value: "2 February 2026",
          timestamp: "2026-02-02T11:15:30.000Z",
        },
        f5: {
          fieldId: "f5",
          type: "checkbox" as const,
          value: "true",
          timestamp: "2026-02-02T11:15:30.000Z",
        },
      },
    },
    {
      email: "jane.smith@partner.org",
      keyId: "pgp-key-jane-def456ghi789",
      signature:
        "sig-jane-abc123def456ghi789jkl012mno345pqr678stu901vwx234yz567",
      timestamp: "2026-02-02T14:22:15.000Z",
      serverSignature: {
        signerEmail: "jane.smith@partner.org",
        signerKeyId: "pgp-key-jane-def456ghi789",
        docHash: "mock-doc-hash-abc123",
        ip: "198.51.100.88",
        location: "London, UK",
        timestamp: "2026-02-02T14:22:15.000Z",
        serverKeyId: "alpha-1",
        verified: true,
        witnessed: true,
        signature: "mock-ecdsa-sig-jane",
        userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64)",
      },
      sessionInput: {
        f6: {
          fieldId: "f6",
          type: "signature" as const,
          value:
            '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="60"><text x="10" y="40" font-family="cursive" font-size="30" fill="darkblue">Jane Smith</text></svg>',
          fontFamily: "cursive",
        },
        f7: {
          fieldId: "f7",
          type: "name" as const,
          value: "Jane Smith",
        },
        f8: {
          fieldId: "f8",
          type: "text" as const,
          value: "Acme Corporation Ltd.",
        },
      },
    },
    {
      email: "bob.wilson@client.net",
      keyId: "pgp-key-bob-ghi789jkl012",
      signature:
        "sig-bob-def456ghi789jkl012mno345pqr678stu901vwx234yz567abc890",
      timestamp: "2026-02-02T16:45:00.000Z",
      serverSignature: {
        signerEmail: "bob.wilson@client.net",
        signerKeyId: "pgp-key-bob-ghi789jkl012",
        docHash: "mock-doc-hash-abc123",
        ip: "192.0.2.150",
        location: "Tokyo, Japan",
        timestamp: "2026-02-02T16:45:00.000Z",
        serverKeyId: "alpha-1",
        verified: true,
        witnessed: true,
        signature: "mock-ecdsa-sig-bob",
        userAgent: "Mozilla/5.0 (X11; Linux x86_64)",
      },
      sessionInput: {
        f9: {
          fieldId: "f9",
          type: "signature" as const,
          value:
            '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="60"><text x="10" y="40" font-family="cursive" font-size="32" fill="darkblue">Bob Wilson</text></svg>',
          fontFamily: "cursive",
        },
        f10: {
          fieldId: "f10",
          type: "date" as const,
          value: "15 January 2026",
          timestamp: "2026-02-02T16:45:00.000Z",
        },
      },
    },
  ];

  const generateMockPDF = async () => {
    setGenerating(true);
    try {
      const pdfDoc = await PDFDocument.create();
      const theme = await loadPdfTheme(pdfDoc);
      const { helvetica } = theme;

      const renderFieldWrapper = (filled: any, pxW: number, pxH: number) =>
        renderFieldToImage(pdfDoc, filled, pxW, pxH);

      // 1. Generate Intro Page
      await generateIntroPage(pdfDoc, {
        isComplete: false,
        documentTitle: "Mock Document Debug",
        existingMetadata: mockMetadata,
        theme,
      });

      // 2. Add an intentionally blank page
      const blankPage = pdfDoc.addPage([595, 842]);
      blankPage.drawText("THIS PAGE INTENTIONALLY BLANK", {
        x: 180,
        y: 421,
        size: 14,
        font: helvetica,
        color: rgb(0.8, 0.8, 0.8),
      });

      // 3. Generate the meta page with mock data
      await generateMetaPage(
        pdfDoc,
        {
          documentHash: mockMetadata.documentHash,
          isLastSigner: true, // All signers have signed
          existingMetadata: mockMetadata,
          updatedSignatures: mockSignatures,
        },
        {
          pdfDoc,
          ...theme,
          renderFieldToImage: renderFieldWrapper,
        },
      );

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as any], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      // Revoke old URL to prevent memory leaks
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);

      setPdfUrl(url);
      setLastGenerated(new Date());
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      alert(`Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setGenerating(false);
    }
  };

  // Auto-generate on mount
  useEffect(() => {
    generateMockPDF();
  }, []);

  // Cleanup URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "#1a1a1a",
      }}
    >
      <Paper
        p="md"
        style={{
          borderRadius: 0,
          background: "#2a2a2a",
          borderBottom: "1px solid #444",
        }}
      >
        <Group justify="space-between">
          <div>
            <Text size="lg" fw={700} c="white">
              Meta Page Layout Debugger
            </Text>
            <Text size="xs" c="dimmed">
              Edit <Code>/src/lib/meta-page.ts</Code> and click Regenerate to
              see changes
            </Text>
          </div>
          <Group>
            {lastGenerated && (
              <Text size="xs" c="dimmed">
                Last generated: {lastGenerated.toLocaleTimeString()}
              </Text>
            )}
            <Button
              onClick={generateMockPDF}
              loading={generating}
              leftSection={<span>🔄</span>}
            >
              Regenerate PDF
            </Button>
          </Group>
        </Group>
      </Paper>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        {pdfUrl ? (
          <iframe
            ref={iframeRef}
            src={pdfUrl}
            style={{
              width: "100%",
              maxWidth: "900px",
              height: "100%",
              border: "2px solid #444",
              borderRadius: 8,
              background: "white",
            }}
            title="Meta Page Preview"
          />
        ) : (
          <Text c="dimmed">Generating PDF...</Text>
        )}
      </div>

      <Paper
        p="sm"
        style={{
          borderRadius: 0,
          background: "#2a2a2a",
          borderTop: "1px solid #444",
        }}
      >
        <Stack gap="xs">
          <Text size="xs" c="dimmed" fw={700}>
            Mock Data Summary:
          </Text>
          <Group gap="xl">
            <Text size="xs" c="white">
              📄 Document Hash:{" "}
              <Code>{mockMetadata.documentHash.slice(0, 16)}...</Code>
            </Text>
            <Text size="xs" c="white">
              👥 Signers: {mockMetadata.signers.length}
            </Text>
            <Text size="xs" c="white">
              ✍️ Signatures: {mockSignatures.length}
            </Text>
            <Text size="xs" c="white">
              📝 Fields: {mockMetadata.fields.length}
            </Text>
          </Group>
        </Stack>
      </Paper>
    </div>
  );
}
